import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { QRCodeSVG } from "qrcode.react";
import { Trans, useLingui } from "@lingui/macro";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import API from "@app/services/api";
import { refreshUser } from "@app/utils/use-most-fresh-user";
import { signOut } from "@app/utils/sign-out";
import useUser from "@app/zustand/user";
import PscLoginButton from "@app/components/PscLoginButton";
import {
  formatDeadline,
  owesTwoFactors,
  type AuthPolicyPayload,
} from "@app/utils/auth-policy";

interface PasswordResponse {
  set?: boolean;
  detail?: string;
  error?: string;
}

interface SetupResponse {
  secret?: string;
  provisioning_uri?: string;
  detail?: string;
  error?: string;
}

interface ConfirmResponse {
  enabled?: boolean;
  detail?: string;
  error?: string;
}

interface PscStatusResponse {
  available?: boolean;
}

interface AuthSetupWizardProps {
  /** The signed-in account's `auth` block, as the API serves it. */
  policy: AuthPolicyPayload;
}

/**
 * The compliance wizard: a password, an authenticator app, and for a doctor the
 * Pro Santé Connect card.
 *
 * It stands in for the application whenever the session is a restricted
 * enrollment one — that is, whenever something the account owes is past the end
 * of its window. It is not the wizard that blocks: the server already refuses
 * every other call. The wizard is what is left on screen when it does, and the way
 * out of it.
 *
 * It calls exactly the endpoints Configuration → Compte calls. Nothing is
 * duplicated here: each step that settles the last overdue item turns the
 * restricted session into a real one server-side, and all that is left to do is
 * re-read the user. The steps follow the `auth` block, so a doctor who still owes
 * the card after the authenticator lands on the card step, not on a finished
 * wizard.
 */
export default function AuthSetupWizard({ policy }: AuthSetupWizardProps) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const setUser = useUser((state) => state.setUser);
  const [needsPassword, setNeedsPassword] = useState(
    policy.missing_factors.includes("password"),
  );
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [secret, setSecret] = useState("");
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Set before the call and never reset: without it a failure would leave `uri`
  // empty, the effect would fire again on the next render, and the screen would call
  // the server in a loop instead of showing the error once.
  const [setupStarted, setSetupStarted] = useState(false);
  // The card step is offered here rather than only in the settings page because a
  // restricted session never reaches the settings page.
  // Null until the status call answers, so the card step does not flash
  // "unavailable" before it knows.
  const [pscAvailable, setPscAvailable] = useState<boolean | null>(null);

  const totpOwed = policy.missing_factors.includes("totp");
  const cardOwed = policy.missing_factors.includes("psc");
  const step = needsPassword ? 0 : totpOwed ? 1 : 2;
  const steps = [
    t`1. Mot de passe`,
    t`2. Application d'authentification`,
    ...(policy.psc_required ? [t`3. Carte CPS ou e-CPS`] : []),
  ];

  useEffect(() => {
    API.get({ path: "/user/psc/status" }).then((res: PscStatusResponse) => {
      setPscAvailable(Boolean(res?.available));
    });
  }, []);

  // The QR code is prepared as soon as the password is in place: TOTP enrollment
  // requires one server-side, and starting here spares a click whose only effect
  // would be to wait.
  useEffect(() => {
    if (needsPassword || !totpOwed || setupStarted) return;
    setSetupStarted(true);
    setBusy(true);
    API.post({ path: "/user/totp/setup" })
      .then((res: SetupResponse) => {
        if (res?.secret && res?.provisioning_uri) {
          setSecret(res.secret);
          setUri(res.provisioning_uri);
        } else {
          setError(
            res?.detail ||
              res?.error ||
              t`Impossible de démarrer la configuration.`,
          );
        }
      })
      .finally(() => setBusy(false));
  }, [needsPassword, totpOwed, setupStarted, t]);

  function submitPassword() {
    if (password !== confirmPassword) {
      setError(t`Les deux mots de passe ne sont pas identiques.`);
      return;
    }
    setBusy(true);
    setError("");
    API.post({ path: "/user/password/set", body: { new_password: password } })
      .then((res: PasswordResponse) => {
        if (res?.set) {
          setPassword("");
          setConfirmPassword("");
          setNeedsPassword(false);
        } else {
          setError(
            res?.detail ||
              res?.error ||
              t`Impossible d'enregistrer le mot de passe.`,
          );
        }
      })
      .finally(() => setBusy(false));
  }

  function submitCode() {
    if (code.length !== 6) return;
    setBusy(true);
    setError("");
    API.post({ path: "/user/totp/confirm", body: { code } })
      .then(async (res: ConfirmResponse) => {
        if (res?.enabled) {
          // If nothing else is overdue, the server has just swapped the restricted
          // cookie for a real session and re-reading the user makes the wizard
          // disappear; if the card is, the new `auth` block moves it to that step.
          setCode("");
          await refreshUser("AuthSetupWizard");
        } else {
          setError(res?.detail || res?.error || t`Code invalide. Réessayez.`);
        }
      })
      .finally(() => setBusy(false));
  }

  // Password + TOTP have no window; only the card's can have ended.
  const deadline = owesTwoFactors(policy)
    ? null
    : formatDeadline(policy.psc_deadline);

  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-xl space-y-6">
        <div className="flex justify-center">
          <img
            src="/logo.png"
            alt="MAGE-X"
            className="h-8 [filter:brightness(0)_saturate(100%)_invert(0%)_sepia(95%)_saturate(5549%)_hue-rotate(235deg)_brightness(6%)_contrast(108%)]"
          />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            <Trans>Sécurisez votre compte</Trans>
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            <Trans>
              L'accès aux données de santé impose désormais deux facteurs de
              connexion : un mot de passe et un code à usage unique généré par
              une application. Le code reçu par email ne peut plus servir de
              moyen de connexion.
            </Trans>
          </p>
          {policy.psc_required && (
            <p className="mt-2 text-sm text-slate-600">
              <Trans>
                Les médecins doivent en outre associer leur carte CPS ou leur
                e-CPS (Pro Santé Connect). Le mot de passe et l'application
                restent leur accès de secours si Pro Santé Connect est
                indisponible.
              </Trans>
            </p>
          )}
          {deadline && (
            <p className="mt-2 text-sm text-slate-500">
              <Trans>
                Le délai accordé à votre compte a pris fin le {deadline}.
              </Trans>
            </p>
          )}

          <ol className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-b border-slate-100 pb-4 text-sm">
            {steps.map((label, index) => (
              <li
                key={label}
                className={
                  index === step
                    ? "font-semibold text-primary"
                    : index < step
                      ? "text-slate-400 line-through"
                      : "text-slate-400"
                }
              >
                {label}
              </li>
            ))}
          </ol>

          {step === 2 ? (
            <div className="mt-6 space-y-4">
              {pscAvailable === null ? null : cardOwed && pscAvailable ? (
                <>
                  <p className="text-sm text-slate-600">
                    <Trans>
                      Associez votre carte CPS ou votre e-CPS : vous serez
                      redirigé vers Pro Santé Connect, puis ramené sur MAGE-X.
                    </Trans>
                  </p>
                  {/* The official button, alone: the charter forbids text right beside it. */}
                  <PscLoginButton intent="link" />
                </>
              ) : (
                <p className="text-sm text-slate-600">
                  <Trans>
                    Pro Santé Connect est momentanément indisponible. Réessayez
                    dans quelques instants.
                  </Trans>
                </p>
              )}
            </div>
          ) : needsPassword ? (
            <form
              className="mt-6 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                submitPassword();
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="setup-password">
                  <Trans>Choisissez un mot de passe</Trans>
                </Label>
                <Input
                  id="setup-password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  <Trans>
                    Au moins 12 caractères, combinant au moins trois types parmi
                    : minuscules, majuscules, chiffres, caractères spéciaux.
                  </Trans>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="setup-password-confirm">
                  <Trans>Confirmer le mot de passe</Trans>
                </Label>
                <Input
                  id="setup-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button
                type="submit"
                className="bg-primary text-white"
                disabled={busy || !password || !confirmPassword}
              >
                {busy ? (
                  <Trans>Enregistrement...</Trans>
                ) : (
                  <Trans>Continuer</Trans>
                )}
              </Button>
            </form>
          ) : (
            <div className="mt-6 space-y-4">
              <p className="text-sm text-slate-600">
                <Trans>
                  Scannez ce QR code avec votre application d'authentification
                  (Google Authenticator, Authy, e-CPS), ou saisissez la clé
                  manuellement.
                </Trans>
              </p>
              <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:flex-row">
                <div className="rounded-md bg-white p-3">
                  {uri ? (
                    <QRCodeSVG value={uri} size={160} />
                  ) : (
                    <div className="flex h-[160px] w-[160px] items-center justify-center text-xs text-slate-400">
                      <Trans>Préparation...</Trans>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-500">
                    <Trans>Clé (saisie manuelle)</Trans>
                  </Label>
                  <code className="block break-all rounded bg-white px-2 py-1 font-mono text-sm text-slate-900">
                    {secret}
                  </code>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="setup-code">
                  <Trans>
                    Saisissez le code à 6 chiffres affiché par l'application
                  </Trans>
                </Label>
                <Input
                  id="setup-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder={t`Ex. : 123456`}
                  className="max-w-[200px] tracking-[0.4em]"
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button
                onClick={submitCode}
                className="bg-primary text-white"
                disabled={busy || code.length !== 6}
              >
                {busy ? (
                  <Trans>Vérification...</Trans>
                ) : cardOwed ? (
                  <Trans>Continuer</Trans>
                ) : (
                  <Trans>Terminer et accéder à MAGE-X</Trans>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* The wizard stands in for the whole application, header included, so its
            sign-out lives here. Left open on a shared workstation, an enrollment session
            lets the next person enroll their own phone on this account. */}
        <div className="text-center">
          <button
            type="button"
            onClick={() =>
              signOut(() => {
                setUser(null);
                navigate("/welcome");
              })
            }
            className="text-sm text-slate-500 transition-colors hover:text-primary"
          >
            <Trans>Se déconnecter</Trans>
          </button>
        </div>
      </div>
    </div>
  );
}
